import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { Slack, MessageSquare } from 'lucide-react';

interface Integration {
  id: string;
  integration_type: string;
  webhook_url: string;
  is_active: boolean;
}

export function DistrictIntegrations() {
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [slackWebhook, setSlackWebhook] = useState('');
  const [teamsWebhook, setTeamsWebhook] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userProfile?.district_id) {
      loadIntegrations();
    }
  }, [userProfile]);

  const loadIntegrations = async () => {
    const { data, error } = await supabase
      .from('district_integrations')
      .select('*')
      .eq('district_id', userProfile?.district_id);

    if (error) {
      console.error('Error loading integrations:', error);
      return;
    }

    setIntegrations(data || []);
    
    // Populate form fields
    data?.forEach((integration) => {
      if (integration.integration_type === 'slack') {
        setSlackWebhook(integration.webhook_url);
      } else if (integration.integration_type === 'teams') {
        setTeamsWebhook(integration.webhook_url);
      }
    });
  };

  const saveIntegration = async (type: 'slack' | 'teams', webhookUrl: string) => {
    if (!webhookUrl.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a webhook URL',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    const existing = integrations.find((i) => i.integration_type === type);

    try {
      if (existing) {
        const { error } = await supabase
          .from('district_integrations')
          .update({ webhook_url: webhookUrl })
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from('district_integrations').insert({
          district_id: userProfile?.district_id,
          integration_type: type,
          webhook_url: webhookUrl,
          created_by: user?.id,
        });

        if (error) throw error;
      }

      toast({
        title: 'Success',
        description: `${type === 'slack' ? 'Slack' : 'Teams'} integration saved`,
      });

      loadIntegrations();
    } catch (error) {
      console.error('Error saving integration:', error);
      toast({
        title: 'Error',
        description: 'Failed to save integration',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleIntegration = async (id: string, isActive: boolean) => {
    const { error } = await supabase
      .from('district_integrations')
      .update({ is_active: isActive })
      .eq('id', id);

    if (error) {
      console.error('Error toggling integration:', error);
      toast({
        title: 'Error',
        description: 'Failed to update integration',
        variant: 'destructive',
      });
    } else {
      loadIntegrations();
      toast({
        title: 'Success',
        description: `Integration ${isActive ? 'enabled' : 'disabled'}`,
      });
    }
  };

  const testIntegration = async (type: 'slack' | 'teams', webhookUrl: string) => {
    if (!webhookUrl.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a webhook URL',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.functions.invoke('test-webhook', {
        body: { type, webhookUrl },
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Test message sent successfully',
      });
    } catch (error) {
      console.error('Error testing integration:', error);
      toast({
        title: 'Error',
        description: 'Failed to send test message',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">District Integrations</h2>
        <p className="text-muted-foreground">
          Connect your district's communication platforms to receive notifications
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Slack className="h-5 w-5" />
            Slack Integration
          </CardTitle>
          <CardDescription>
            Send notifications to your Slack workspace
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="slack-webhook">Webhook URL</Label>
            <Input
              id="slack-webhook"
              placeholder="https://hooks.slack.com/services/..."
              value={slackWebhook}
              onChange={(e) => setSlackWebhook(e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch
                checked={integrations.find((i) => i.integration_type === 'slack')?.is_active}
                onCheckedChange={(checked) => {
                  const integration = integrations.find((i) => i.integration_type === 'slack');
                  if (integration) toggleIntegration(integration.id, checked);
                }}
              />
              <Label>Enable Slack notifications</Label>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => testIntegration('slack', slackWebhook)}
              disabled={loading}
              variant="outline"
            >
              Test Connection
            </Button>
            <Button onClick={() => saveIntegration('slack', slackWebhook)} disabled={loading}>
              Save Integration
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Microsoft Teams Integration
          </CardTitle>
          <CardDescription>
            Send notifications to your Teams channel
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="teams-webhook">Webhook URL</Label>
            <Input
              id="teams-webhook"
              placeholder="https://outlook.office.com/webhook/..."
              value={teamsWebhook}
              onChange={(e) => setTeamsWebhook(e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch
                checked={integrations.find((i) => i.integration_type === 'teams')?.is_active}
                onCheckedChange={(checked) => {
                  const integration = integrations.find((i) => i.integration_type === 'teams');
                  if (integration) toggleIntegration(integration.id, checked);
                }}
              />
              <Label>Enable Teams notifications</Label>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => testIntegration('teams', teamsWebhook)}
              disabled={loading}
              variant="outline"
            >
              Test Connection
            </Button>
            <Button onClick={() => saveIntegration('teams', teamsWebhook)} disabled={loading}>
              Save Integration
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
